export type RenderBackend = 'webgl2' | 'canvas2d'
export type DeviceClass = 'constrained' | 'mobile' | 'desktop'

export const MAX_PARTICLE_COUNT = 100_000

export const PARTICLE_BUDGETS = {
  webgl2: {
    constrained: 24_000,
    mobile: 48_000,
    desktop: MAX_PARTICLE_COUNT,
  },
  canvas2d: {
    constrained: 8_000,
    mobile: 16_000,
    desktop: 32_000,
  },
} as const satisfies Record<RenderBackend, Record<DeviceClass, number>>

export function getParticleBudget(
  backend: RenderBackend,
  deviceClass: DeviceClass,
): number {
  return PARTICLE_BUDGETS[backend][deviceClass]
}

function assertNonNegativeInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} must be a non-negative integer`)
  }
}

/**
 * Produces an ordered, uniformly distributed index map. When at least two
 * entries fit, both source endpoints are retained. No source-sized temporary
 * allocation is created when the source is above budget.
 */
export function createDownsampleIndices(
  inputCount: number,
  budget: number,
): Uint32Array {
  assertNonNegativeInteger(inputCount, 'Input count')
  assertNonNegativeInteger(budget, 'Particle budget')
  if (budget > MAX_PARTICLE_COUNT) {
    throw new RangeError(
      `Particle budget ${budget} exceeds hard cap ${MAX_PARTICLE_COUNT}`,
    )
  }
  if (inputCount > 0xffff_ffff) {
    throw new RangeError('Input count exceeds Uint32 index capacity')
  }

  const outputCount = Math.min(inputCount, budget)
  const indices = new Uint32Array(outputCount)
  if (outputCount === 0) {
    return indices
  }
  if (outputCount === 1) {
    indices[0] = 0
    return indices
  }
  if (outputCount === inputCount) {
    for (let index = 0; index < outputCount; index += 1) {
      indices[index] = index
    }
    return indices
  }

  const lastSourceIndex = inputCount - 1
  const lastOutputIndex = outputCount - 1
  indices[0] = 0
  for (let index = 1; index < lastOutputIndex; index += 1) {
    indices[index] = Math.floor((index * lastSourceIndex) / lastOutputIndex)
  }
  indices[lastOutputIndex] = lastSourceIndex

  return indices
}
